import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import RopoInvoiceDetails from './RopoInvoiceDetails';
import BackButton from '../../../Elements/BackButton';
import InvoicesCalendar from '../../../Elements/InvoicesCalendar';

import { getRopoInvoices } from '../../../../actions/getRopoDetails';
import { ropoInvoice, companyParams, IinvoicesResponse } from '../../../../types/ropo';
// eslint-disable-next-line
// @ts-ignore
import { themeDetails } from '../../../../theme/theme';

const RopoDetails: React.FunctionComponent = () => {
  const [invoices, setInvoices] = useState<ropoInvoice[]>([]);
  const [activeMP, setActiveMP] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);

  const [startDate, setStartDate] = useState<moment.Moment | null>(moment().startOf('year'));
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment().endOf('year'));

  const [onlineMaxSaldoLimit, setOnlineMaxSaldoLimit] = useState<number | null>(null);
  const [shouldShowPostponeColumn, setShouldShowPostponeColumn] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const fetchRopoInvoices = async (): Promise<void> => {
      setLoading(true);
      setErrorMessage('');

      try {
        const invoicingData: IinvoicesResponse | null = await getRopoInvoices();

        if (cancelled) return;

        if (!invoicingData) {
          setInvoices([]);
          setOnlineMaxSaldoLimit(null);
          setShouldShowPostponeColumn(false);
          setErrorMessage('Laskutietoja ei löytynyt.');
          return;
        }

        const { companyParams, invoices: apiInvoices } = invoicingData;

        const onlineMaxSaldoLimitParam = companyParams?.find(
          (param: companyParams) =>
            param.param === "OnlineMaxSaldoLimit"
        );

        const parsedOnlineMaxSaldoLimit = onlineMaxSaldoLimitParam
          ? Number.parseFloat(onlineMaxSaldoLimitParam.value)
          : null;

        setOnlineMaxSaldoLimit(
          parsedOnlineMaxSaldoLimit !== null &&
            Number.isFinite(parsedOnlineMaxSaldoLimit)
            ? parsedOnlineMaxSaldoLimit
            : null
        );

        // Sort newest to oldest
        const sortedInvoices = Array.isArray(apiInvoices)
          ? [...apiInvoices].sort((a, b) => new Date(b.duedate).getTime() - new Date(a.duedate).getTime())
          : [];
        setInvoices(sortedInvoices);
        // If invoice has a max duedate value AND payment agreement is allowed - show the postpone column
        setShouldShowPostponeColumn(
          sortedInvoices.some(
            invoice =>
              invoice.payment_agreement_max_duedate != null &&
              invoice.is_payment_agreement_allowed === true
          )
        );

        if (sortedInvoices.length === 0) {
          setErrorMessage('Laskutietoja ei löytynyt.');
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMessage('Laskutietojen lataaminen epäonnistui. Yritä myöhemmin uudelleen.');
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    fetchRopoInvoices();

    return () => {
      cancelled = true;
    };
  }, []);

  // Build metering point options from invoices and memoize
  const meteringPointOptions = useMemo(() => {
    const uniqueByObjectKey = new Map<string, { objectKey: string; objectName: string; networkId?: string }>();
    for (const invoice of invoices) {
      const periods = invoice.calculation_periods;
      if (!Array.isArray(periods)) continue;

      for (const period of periods) {
        if (!period?.object_key) continue;

        if (!uniqueByObjectKey.has(period.object_key)) {
          uniqueByObjectKey.set(period.object_key, {
            objectKey: period.object_key,
            objectName: period.object_name,
            networkId: period.network_id
          });
        }
      }
    }

    const electricity: JSX.Element[] = [];
    const heating: JSX.Element[] = [];

    for (const mp of uniqueByObjectKey.values()) {
      const option = (
        <option value={mp.objectKey} key={mp.objectKey}>
          {mp.objectName}
        </option>
      );

      // network_id ends with "00" => electricity (000KL for heat)
      if (mp.networkId && mp.networkId.slice(-2) === '00') {
        electricity.push(option);
      } else {
        heating.push(option);
      }
    }

    // Wrap in optgroups only if both exist
    if (electricity.length > 0 && heating.length > 0) {
      return (
        <>
          <optgroup label="Sähkösopimukset">{electricity}</optgroup>
          <optgroup label="Lämmityssopimukset">{heating}</optgroup>
        </>
      );
    }

    return <>{electricity.length > 0 ? electricity : heating}</>;
  }, [invoices]);
  // Memoize this calculation so that we dont do same during every render
  // Recalculates if invoices updates
  //  - which basically shouldnt happen since we fetch all of the invoices at once)
  const meteringPointCount = useMemo(() => {
    // Count options
    const set = new Set<string>();
    for (const invoice of invoices) {
      const periods = invoice.calculation_periods;
      if (!Array.isArray(periods)) continue;
      for (const p of periods) {
        if (p?.object_key) set.add(p.object_key);
      }
    }
    return set.size;
  }, [invoices]);

  // Filter invoices by date range + metering point
  const visibleInvoices = useMemo(() => {
    if (invoices.length === 0) return [];

    return invoices.filter(invoice => {
      const invoiceDate = moment(invoice.duedate);

      const isWithinDateRange =
        startDate != null && endDate != null && invoiceDate.isBetween(startDate, endDate, 'day', '[]');

      const invoiceMp = invoice.calculation_periods?.[0]?.object_key;
      const matchesMeteringPoint = activeMP === 'all' || (invoiceMp != null && invoiceMp === activeMP);

      return Boolean(isWithinDateRange && matchesMeteringPoint);
    });
  }, [invoices, activeMP, startDate, endDate]);

  const updateSelectedMP = (event: ChangeEvent<HTMLSelectElement>): void => {
    setActiveMP(event.target.value);
  };

  let invoiceOptions: JSX.Element | null = null;

  if (invoices.length > 0) {
    if (meteringPointCount > 1) {
      invoiceOptions = (
        <select style={{ marginTop: '16px' }} onChange={updateSelectedMP} value={activeMP}>
          <option value="all">Kaikki</option>
          {meteringPointOptions}
        </select>
      );
    } else {
      // If only one metering point, show the name as title
      const title = invoices[0]?.calculation_periods?.[0]?.object_name;
      invoiceOptions = title ? <p>{title}</p> : null;
    }
  }

  const shouldShowTable = !loading && invoices.length > 0;

  return (
    <div>
      <div className="wrapper contracts">
        <div className="btn-row">
          <BackButton to="/dashboard" />
          <h1>Laskutiedot</h1>
        </div>

        <InvoicesCalendar setStartDate={setStartDate} setEndDate={setEndDate} startDate={startDate} endDate={endDate} />

        {loading && <p>Haetaan laskutietoja</p>}

        {!loading && errorMessage && <p>{errorMessage}</p>}

        {shouldShowTable && (
          <div>
            {invoiceOptions}

            <div className="ropo-table-responsive">
              <table>
                <tbody>
                  <tr>
                    <th>Eräpäivä</th>
                    <th>Laskun numero</th>
                    <th>Laskutyyppi</th>
                    <th>Laskun summa</th>
                    <th>Maksettava</th>
                    <th>Laskun tila</th>
                    <th>Laskun pdf</th>
                    {shouldShowPostponeColumn && <th>Siirrä eräpäivää</th>}
                  </tr>

                  {visibleInvoices.map(invoice => (
                    <RopoInvoiceDetails
                      key={invoice.invoice_number}
                      setInvoices={setInvoices}
                      invoice={invoice}
                      onlineMaxSaldoLimit={onlineMaxSaldoLimit}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {!loading && visibleInvoices.length === 0 && <p>Ei laskuja valitulla suodatuksella.</p>}
          </div>
        )}
      </div>

      {themeDetails.theme === 'tke' && (
        <div className="tke-invoice-support">
          <p>
            Laskun maksamiseen ja myöhästyneisiin maksuihin liittyvissä asioissa sinua palvelee kumppanimme Ropo:{' '}
            <a href="https://ropo.fi" target="_blank" rel="noopener noreferrer">
              ropo.fi
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default RopoDetails;
